
/**
 * My Favorites — the list rules (RM-296).
 *
 * Pure functions, no Vue and no store, so the filters, the sorting and the
 * city/state display rule are unit-tested directly (client/src/utils/favoritesList.spec.js)
 * instead of being buried in the component. MemberFavorites.vue imports these —
 * there is one implementation, not a mirror.
 *
 * Four filters: All | Booked | Notes | Preferred. Three sorts, on every filter:
 * Recent | A-Z | Most booked (RM-396, Kapil 10/12: "we should have A to Z,
 * recent and then most booked", and Booked "should have the same" control).
 * Notes lists restaurants with a note AND the member's General Notes, together.
 *
 * RM AI answers from the same list, in the app and by text: lib/favoritesData.js
 * is the server's copy of the filter and sort rules here. Change both together;
 * __tests__/unit/lib/favoritesData.test.js runs these functions and fails when
 * the two disagree.
 */

export const ALL_CITIES = 'All'

export const FAVORITE_FILTERS = [
  { value: 'All', text: 'All' },
  { value: 'Booked', text: 'Booked' },
  { value: 'Notes', text: 'Notes' },
  { value: 'Preferred', text: 'Preferred' },
]

export const FAVORITE_SORTS = [
  { value: 'Recent', text: 'Recent' },
  { value: 'Alpha', text: 'A-Z' },
  { value: 'MostBooked', text: 'Most booked' },
]

function time(value) {
  if (!value) return 0
  const ms = value instanceof Date ? value.getTime() : new Date(value).getTime()

  return Number.isNaN(ms) ? 0 : ms
}

function byName(a, b) {
  return String(a.name || '').localeCompare(String(b.name || ''))
}

/**
 * How long ago the member last booked here, as the bucket + label the row shows.
 * Same buckets as the previous Favorites card (last mo / 3mo / 6mo / yr / 1yr+ / never).
 */
export function bookingRecency(lastBookingDate, now = new Date()) {
  if (!lastBookingDate) return { period: 'never', rank: 6 }
  const months = (time(now) - time(lastBookingDate)) / (1000 * 60 * 60 * 24 * 30.4375)
  if (months < 1) return { period: 'last mo', rank: 1 }
  if (months < 3) return { period: 'last 3mo', rank: 2 }
  if (months < 6) return { period: 'last 6mo', rank: 3 }
  if (months < 12) return { period: 'last yr', rank: 4 }

  return { period: 'last 1yr+', rank: 5 }
}

/** "Booked 3+ times, incl last 3mo" — the wording the booking card already used. */
export function bookedText(bookingCount, period) {
  if (!bookingCount || !period || period === 'never') return ''
  const timeString = bookingCount >= 3 && period !== 'last 1yr+' ? `incl ${period}` : period
  if (bookingCount >= 100) return `100+ times, ${timeString}`
  if (bookingCount >= 50) return `50+ times, ${timeString}`
  if (bookingCount >= 20) return `20+ times, ${timeString}`
  if (bookingCount >= 10) return `10+ times, ${timeString}`
  if (bookingCount >= 3) return `3+ times, ${timeString}`

  return `in ${timeString}`
}

/**
 * The city selector's options, built from every favorite (independent of the
 * active filter, so the counts don't move as filters change).
 */
export function buildCityOptions(entries) {
  const counts = new Map()
  for (const entry of entries) {
    const market = entry.restaurant.market
    if (!market) continue
    counts.set(market, (counts.get(market) || 0) + 1)
  }
  const cities = [...counts.entries()].sort((a, b) => (b[1] - a[1]) || a[0].localeCompare(b[0]))

  const withCommas = n => n.toLocaleString('en-US')

  return [
    { text: `All Cities (${withCommas(entries.length)} Favorites)`, value: ALL_CITIES },
    ...cities.map(([market, count]) => ({ text: `${market} (${withCommas(count)})`, value: market })),
  ]
}

/**
 * Join the member's favorites with their booking data, notes and network signal.
 * `member` carries the index-aligned arrays from GET /user/me/favorites-data.
 */
export function buildFavoriteEntries({
  restaurants = [],
  member = {},
  favoriteNotes = {},
  friendsFavoritedByMap = {},
  // Only the member's OWN favorites are known-favorited (and only their own notes apply).
  markFavorited = true,
  // Viewing another member's favorites: which of them are also mine ("Shared").
  myFavoriteIds = [],
  /**
   * Viewing another member's favorites: when they last shared a Highlight
   * here, by restaurant id. Their notes are never sent to me, so without this
   * a Highlight they shared a minute ago sat in alphabetical order (Kapil,
   * 2026-09-23). Only what the viewer can see moves a row — a private note's
   * activity would tell them when the member touched a restaurant.
   */
  sharedAtById = {},
  now = new Date(),
} = {}) {
  const favoriteIds = (member.favoritedRestaurants || []).map(id => String(id?._id || id))
  const favIndex = new Map(favoriteIds.map((id, i) => [id, i]))
  const favoritedAt = member.favoritedAt || {}
  // When they last asked for each restaurant — a request through RM, or RM
  // drafting their text (Kapil, 2026-09-25). Only the member's own list (to the
  // second) and a platform admin's view (to the day) get it; everyone else
  // gets the order alone (favoritedRecentOrder, below).
  const lastRequestedAt = member.favoritedLastRequestedAt || {}

  /**
   * Another member's profile (Kapil, 2026-09-26: "The date should never be
   * showing up"): the server sends no booking count or date and no request
   * date — only each Favorite's rounded booking line (connections only) and
   * the order "Recent" goes by, as restaurant ids, most recent first.
   */
  const roundedBookings = Array.isArray(member.favoritedRestaurantsBooked)
    ? member.favoritedRestaurantsBooked
    : null
  const recentOrder = Array.isArray(member.favoritedRecentOrder) ? member.favoritedRecentOrder : null
  const recentRank = recentOrder
    ? new Map(recentOrder.map((rid, n) => [String(rid), recentOrder.length - n]))
    : null
  const mine = new Set((myFavoriteIds || []).map(id => String(id?._id || id)))

  const entries = []
  for (const restaurant of restaurants) {
    const id = String(restaurant?._id || restaurant?.id || '')
    if (!id || !favIndex.has(id)) continue
    const i = favIndex.get(id)
    const booked = roundedBookings ? roundedBookings[i] || null : null
    const bookingCount = roundedBookings
      ? (booked ? booked.times || 1 : 0)
      : (member.favoritedRestaurantsBookingCount || [])[i] || 0
    const lastBookingDate = roundedBookings
      ? null
      : (member.favoritedRestaurantsLastBookingDate || [])[i] || null
    const lastSubmittedDate = (member.favoritedRestaurantsLastSubmittedDate || [])[i] || null
    // The note exactly as the member wrote it, restaurant name included (RM-346).
    const note = favoriteNotes[id]?.note || ''
    const noteUpdatedAt = favoriteNotes[id]?.updatedAt || null
    // Which entry the shown note IS, so the row can say what that note is —
    // Private Only or shared — rather than what the restaurant's notes are.
    const noteEntryId = favoriteNotes[id]?.entryId || null
    // A saved person and the card they came from, so the row shows what the
    // restaurant's My Notes shows — a member should not have to open the
    // restaurant to see a card they just saved (Kapil, 2026-09-23).
    const noteContact = favoriteNotes[id]?.contact || null
    const noteCardImageId = favoriteNotes[id]?.cardImageId || null
    const noteContactEntryId = favoriteNotes[id]?.contactEntryId || null
    const contactUpdatedAt = favoriteNotes[id]?.contactUpdatedAt || null
    // What the member said about the person — shown between who they are and
    // the card (Kapil, 2026-09-23). None for a card sent with no words.
    const contactWords = noteContact
      ? (favoriteNotes[id]?.contactWords || '')
      : ''
    // The person IS the note when the member's newest words are about them —
    // one block, not the same words twice.
    const contactIsNote = !!noteContact && !!noteEntryId && noteContactEntryId === noteEntryId
    /**
     * Newest first (Kapil, 2026-09-23). The row shows the newest note and the
     * newest person, and it always put the note first — so a card saved today
     * sat under a note from two months ago, while the restaurant's My Notes
     * listed it on top.
     */
    const contactFirst = !!noteContact
      && (contactIsNote || !note || time(contactUpdatedAt) > time(noteUpdatedAt))
    // How many dated Profile entries this restaurant has — the row shows the newest
    // and offers "+N more notes" (Founder review, 2026-09-17: a new note never replaces an old one).
    const noteCount = favoriteNotes[id]?.count || (note ? 1 : 0)

    /**
     * "+N more" = what the notes pop-up lists that this row does not already
     * show: the row shows one note and one saved person, which may be the same
     * entry (2026-09-23). It used to be the count of notes minus one, so a
     * row with a saved person said "+1 more note" over a pop-up of two and a
     * My Notes tab of four.
     */
    const total = favoriteNotes[id]?.total
    const shown = new Set([noteEntryId, noteContactEntryId].filter(Boolean)).size
    const noteMore = typeof total === 'number' ? Math.max(0, total - shown) : Math.max(0, noteCount - 1)
    const { period, rank } = roundedBookings
      ? { period: null, rank: booked ? booked.rank : 6 }
      : bookingRecency(lastBookingDate, now)

    const friendsWhoFavorited = friendsFavoritedByMap[id] || []

    entries.push({
      kind: 'restaurant',
      id,
      // This object IS what the row component receives, so everything the row reads must be
      // on it: the heart reads isFavorited (own list only), the network line reads
      // friendsWhoFavorited. Building it here is what the specs assert.
      restaurant: {
        ...restaurant,
        ...(markFavorited ? { isFavorited: true } : {}),
        ...(friendsWhoFavorited.length ? { friendsWhoFavorited } : {}),
      },
      name: restaurant.name,
      market: restaurant.market || '',
      isPreferredRestaurant: restaurant.status === 'Preferred',
      isMyFavorite: markFavorited || mine.has(id),
      friendsWhoFavorited,
      note,
      noteUpdatedAt,
      noteEntryId,
      noteContact,
      noteCardImageId,
      contactUpdatedAt,
      contactWords,
      contactIsNote,
      contactFirst,
      noteCount,
      noteMore,
      bookingCount,
      lastBookingDate,
      lastBookingPeriod: period,
      bookingRank: rank,
      bookedText: roundedBookings ? (booked ? booked.text || '' : '') : bookedText(bookingCount, period),
      // Recency: when they favorited it, or failing that their latest activity on it.
      // A favorite added just now therefore sorts to the top — and so does one
      // whose note or saved person they just touched (Kapil, 2026-09-23: "when
      // someone updates a note, it should also make it the most recent favorite").
      // On another member's profile the server's order stands in for all of
      // it (its place in favoritedRecentOrder; the rest A-Z after it).
      recencyAt: recentRank
        ? (recentRank.get(id) || 0)
        : Math.max(
          time(favoritedAt[id]),
          time(lastSubmittedDate),
          time(noteUpdatedAt),
          time(contactUpdatedAt),
          time(lastBookingDate),
          time(sharedAtById[id]),
          time(lastRequestedAt[id]),
        ),
    })
  }

  return entries
}

/**
 * Rows for restaurant-specific notes on places the member has NOT favorited
 * (GET /restaurant/unfavorited-notes). Founder review, 2026-09-17: these belong under
 * Favorites → Notes as ordinary restaurant rows — saying no to the Favorites question
 * still saved the words, and the member must be able to see them. General notes (no
 * restaurant) are NOT here; they live on the Dining Profile.
 */
export function buildUnfavoritedNoteEntries(rows = [], { friendsFavoritedByMap = {}, now = Date.now() } = {}) {
  return rows
    .filter(r => r && r.restaurantId && r.note)
    .map(r => {
      const id = String(r.restaurantId)
      const friendsWhoFavorited = friendsFavoritedByMap[id] || []

      // A Notes row is an ordinary restaurant row, so a place the member
      // booked through RM says so whether or not it is still a Favorite. The
      // server sends the same counts the favorites path uses.
      const bookingCount = r.bookingCount || 0
      const lastBookingDate = r.lastBookingDate || null
      const { period, rank } = bookingRecency(lastBookingDate, now)

      return {
        kind: 'restaurant',
        id,
        restaurant: {
          ...(r.restaurant || {}),
          _id: id,
          isFavorited: false,
          ...(friendsWhoFavorited.length ? { friendsWhoFavorited } : {}),
        },
        name: r.restaurant?.name || '',
        market: r.restaurant?.market || '',
        isPreferredRestaurant: r.restaurant?.status === 'Preferred',
        isMyFavorite: false,
        friendsWhoFavorited,
        note: r.note || '',
        noteUpdatedAt: r.updatedAt || null,
        noteEntryId: r.entryId || null,
        noteCount: r.count || 1,
        bookingCount,
        lastBookingDate,
        lastBookingPeriod: period,
        bookingRank: rank,
        bookedText: bookedText(bookingCount, period),
        recencyAt: time(r.updatedAt),
      }
    })
}

function matchesFilter(entry, filter) {
  if (filter === 'Booked') return entry.bookingCount > 0
  // A saved card IS a note — RM tells the member it went "to your private
  // notes" — so a restaurant whose only entry is a card belongs here too.
  if (filter === 'Notes') return !!entry.note || !!entry.noteContact
  if (filter === 'Preferred') return entry.isPreferredRestaurant
  // 'Shared' exists only on another member's profile: their favorites that are also mine.
  if (filter === 'Shared') return entry.isMyFavorite

  return true // All
}

function sortEntries(items, { filter, sort }) {
  const rows = [...items]
  if (sort === 'MostBooked') {
    // How often, then how recently, then name. Places never booked follow, A-Z.
    rows.sort((a, b) => (b.bookingCount - a.bookingCount)
      || (a.bookingRank - b.bookingRank)
      || byName(a, b))

    return rows
  }
  if (sort === 'Alpha') {
    // General Notes have no name; they keep their own recency order after the restaurants.
    rows.sort(byName)

    return rows
  }
  if (filter === 'Booked') {
    // Recent, on a list of booked places: most recently booked first, then how often, then name.
    rows.sort((a, b) => (a.bookingRank - b.bookingRank)
      || (b.bookingCount - a.bookingCount)
      || byName(a, b))

    return rows
  }
  rows.sort((a, b) => (b.recencyAt - a.recencyAt) || byName(a, b))

  return rows
}

/**
 * The whole view: the rows to render, the city options, and whether rows should
 * show their city (only when All Cities is selected — otherwise it repeats the
 * city the member just chose).
 *
 * @returns {{ rows: Array, cityOptions: Array, showMarket: boolean, totalFavorites: number }}
 */
export function buildFavoritesView({
  restaurants = [],
  member = {},
  favoriteNotes = {},
  // Restaurant-specific notes on places that are NOT favorites (shown under Notes)
  unfavoritedNotes = [],
  friendsFavoritedByMap = {},
  filter = 'All',
  sort = 'Recent',
  city = ALL_CITIES,
  markFavorited = true,
  myFavoriteIds = [],
  sharedAtById = {},
  now = new Date(),
} = {}) {
  const entries = buildFavoriteEntries({ restaurants, member, favoriteNotes, friendsFavoritedByMap, markFavorited, myFavoriteIds, sharedAtById, now })
  const cityOptions = buildCityOptions(entries)

  let rows = entries.filter(entry => matchesFilter(entry, filter))
  if (city && city !== ALL_CITIES) rows = rows.filter(entry => entry.market === city)

  // Under Notes, a restaurant the member wrote about but did not favorite is a row too.
  if (filter === 'Notes') {
    const already = new Set(rows.map(entry => entry.id))
    let extra = buildUnfavoritedNoteEntries(unfavoritedNotes, { friendsFavoritedByMap })

    // Favoriting from this list makes the restaurant a Favorite row; the feed it came from
    // is only refetched afterwards, so drop the duplicate rather than render it twice.
    extra = extra.filter(entry => !already.has(entry.id))
    if (city && city !== ALL_CITIES) extra = extra.filter(entry => entry.market === city)
    rows = rows.concat(extra)
  }

  return {
    rows: sortEntries(rows, { filter, sort }),
    cityOptions,
    showMarket: !city || city === ALL_CITIES,
    totalFavorites: entries.length,
  }
}
